import { Box, Divider, FormControl, FormControlLabel, FormLabel, Radio, RadioGroup, Stack } from '@mui/material';
import { ChipAutocomplete } from '@synergy/components';
import { Field, FieldProps, Formik, FormikHelpers } from 'formik';
import { FormattedMessage } from 'react-intl';
import { MinimalAccount } from '@synergy/models';
import {
    PoolBanner,
    PoolButton,
    PoolFormikCheckbox,
    PoolModal,
    PoolModalActions,
    PoolModalContent,
    PoolModalProps,
    PoolModalTitle,
    PoolTextfield,
    PoolTypography,
} from '@synergy/components-next';
import { PoolInstance, isInstance } from '@synergy/config';
import { ReactElement, ReactNode, useEffect } from 'react';
import { fields, nullIfEmpty } from '@synergy/utils';
import { useParams } from 'react-router-dom';

import { WishList } from '../../../models/wish-list';
import { WishListType } from '../../../models/enums/wish-list-type';
import { accountSelector, accountsSelector } from '../../../store/configuration-selectors';
import { customersSelector } from '../../../store/customer-selectors';
import { fetchCustomers } from '../../../store/customer-actions';
import { shareWishList } from '../../../store/whish-list-actions';
import { useAppDispatch, useAppSelector } from '../../../../store/store';
import { wishListDetailSelector } from '../../../store/whish-list-selectors';

interface FormValues {
    type: WishListType;
    infoText: string;
    sharedAccounts: MinimalAccount[];
    isSharedWithOwnAccounts: boolean;
    isSharedWithOtherAccountUsers: boolean;
}

const fieldNames = fields<FormValues>();

interface WishListShareModalProps extends Omit<PoolModalProps, 'onClose'> {
    onClose: () => void;
}

export const WishListShareModal = (props: WishListShareModalProps): ReactElement => {
    const { onClose, ...modalProps } = props;
    const wishList: WishList | null = useAppSelector(wishListDetailSelector);
    const { id: paramId = '' } = useParams<{ id?: string }>();
    const currentAccount = useAppSelector(accountSelector);
    const accountOptions: MinimalAccount[] = useAppSelector(accountsSelector);
    const isPoolAccount = useAppSelector(state => state.configuration.isPoolAccount);
    const wishListId = +paramId;
    const dispatch = useAppDispatch();
    const sharedAccounts = wishList?.sharedAccounts ?? [];
    const poolAccountOptions: MinimalAccount[] = useAppSelector(customersSelector);

    useEffect(() => {
        if (isPoolAccount && isInstance(PoolInstance.CH)) {
            dispatch(fetchCustomers(new URLSearchParams({ excludeIds: sharedAccounts.map(v => v.id).join(',') })));
        }
    }, [dispatch, isPoolAccount]);

    const canShareWithMultipleAccounts = isInstance(PoolInstance.CH) && (accountOptions.length > 1 || isPoolAccount);

    const handleSubmit = (values: FormValues, formikProps: FormikHelpers<FormValues>): void => {
        if (!currentAccount) {
            return;
        }

        const updatedSharedAccounts = canShareWithMultipleAccounts
            ? values.sharedAccounts.map((account: MinimalAccount) => account.id)
            : [currentAccount.id];

        dispatch(
            shareWishList(
                wishListId,
                values.type,
                values.isSharedWithOtherAccountUsers ? updatedSharedAccounts : [],
                values.isSharedWithOwnAccounts,
                values.isSharedWithOtherAccountUsers,
                nullIfEmpty(values.infoText)
            )
        );

        handleClose(formikProps);
    };

    const handleClose = (formikProps: FormikHelpers<FormValues>): void => {
        onClose();

        formikProps.resetForm();
    };

    const initialValues: FormValues = {
        type: wishList?.type ?? WishListType.OrganizationRead,
        infoText: wishList?.infoText ?? '',
        sharedAccounts: sharedAccounts,
        isSharedWithOwnAccounts: wishList?.isSharedWithOwnAccounts ?? false,
        isSharedWithOtherAccountUsers: wishList?.isSharedWithOtherAccountUsers ?? false,
    };

    const handlePoolAccountAutocompleteTextFieldChange = (value: string, excludeIds: number[]): void => {
        if (value) {
            dispatch(fetchCustomers(new URLSearchParams({ search: value, excludeIds: excludeIds.join(',') })));
        }
    };

    function renderPoolAccountAutocomplete({ field, form }: FieldProps<MinimalAccount[]>): ReactNode {
        return (
            <ChipAutocomplete
                value={field.value}
                noOptionsText={<FormattedMessage id="app.common.emptyResult" />}
                options={poolAccountOptions}
                getOptionLabel={(account: MinimalAccount) => account.name}
                getChipLabel={(account: MinimalAccount) => account.name}
                textfieldPlaceholder="app.wishList.share.selectAccounts"
                onTextFieldValueChange={(_, value) => {
                    return handlePoolAccountAutocompleteTextFieldChange(
                        value,
                        form.values?.sharedAccounts?.map((v: MinimalAccount) => v.id)
                    );
                }}
                onChange={(_event, value) => {
                    form.setFieldValue(field.name, value);
                }}
            />
        );
    }

    function renderAccountAutocomplete({ field, form }: FieldProps<MinimalAccount[]>): ReactNode {
        return (
            <ChipAutocomplete
                value={field.value}
                noOptionsText={<FormattedMessage id="app.common.emptyResult" />}
                options={accountOptions}
                getOptionLabel={(account: MinimalAccount) => account.name}
                getChipLabel={(account: MinimalAccount) => account.name}
                textfieldPlaceholder="app.wishList.share.selectAccounts"
                onChange={(_event, value) => {
                    form.setFieldValue(field.name, value);
                }}
            />
        );
    }

    return (
        <Formik initialValues={initialValues} onSubmit={handleSubmit} enableReinitialize={true}>
            {formikProps => {
                const canSelectAccounts =
                    canShareWithMultipleAccounts && formikProps.values.isSharedWithOtherAccountUsers;

                return (
                    <PoolModal {...modalProps} onClose={() => handleClose(formikProps)}>
                        <PoolModalTitle>
                            <FormattedMessage id="app.wishList.share.modalTitle" />
                        </PoolModalTitle>
                        <PoolModalContent sx={{ textAlign: 'start' }}>
                            <Stack gap={2} typography="body1">
                                <Stack gap={0.5}>
                                    {isInstance(PoolInstance.CH) && (
                                        <>
                                            <PoolFormikCheckbox
                                                name={fieldNames.isSharedWithOwnAccounts}
                                                disabled={wishList?.customerAccount?.id !== currentAccount?.id}
                                                label={
                                                    <Stack>
                                                        <PoolTypography
                                                            typography="subtitle1"
                                                            translationId="app.wishList.share.shareWithOwnAccounts"
                                                        />
                                                        <PoolTypography
                                                            typography="body2"
                                                            translationId="app.wishList.share.shareWithOwnAccountsDescription"
                                                        />
                                                    </Stack>
                                                }
                                            />

                                            <Divider />
                                        </>
                                    )}

                                    <PoolFormikCheckbox
                                        name={fieldNames.isSharedWithOtherAccountUsers}
                                        label={
                                            <Stack>
                                                {canShareWithMultipleAccounts ? (
                                                    <>
                                                        <PoolTypography
                                                            typography="subtitle1"
                                                            translationId="app.wishList.share.shareWithOtherAccountUsers.multiple"
                                                        />
                                                        <PoolTypography
                                                            typography="body2"
                                                            translationId="app.wishList.share.shareWithOtherAccountUsersDescription.multiple"
                                                        />
                                                    </>
                                                ) : (
                                                    <>
                                                        <PoolTypography
                                                            typography="subtitle1"
                                                            translationId="app.wishList.share.shareWithOtherAccountUsers.one"
                                                        />
                                                        <PoolTypography
                                                            typography="body2"
                                                            translationId="app.wishList.share.shareWithOtherAccountUsersDescription.one"
                                                            values={{ value: wishList?.name }}
                                                        />
                                                    </>
                                                )}
                                            </Stack>
                                        }
                                    />
                                </Stack>

                                {formikProps.values.isSharedWithOtherAccountUsers && (
                                    <Stack gap={0.5}>
                                        {canSelectAccounts && (
                                            <>
                                                <PoolTypography
                                                    typography="subtitle1"
                                                    translationId="app.wishList.share.shareWithOtherAccountUsers.multiple"
                                                />
                                                <Field name={fieldNames.sharedAccounts}>
                                                    {(formikProps: FieldProps<MinimalAccount[]>) => {
                                                        if (isPoolAccount && isInstance(PoolInstance.CH)) {
                                                            return renderPoolAccountAutocomplete(formikProps);
                                                        }

                                                        return renderAccountAutocomplete(formikProps);
                                                    }}
                                                </Field>
                                            </>
                                        )}

                                        <Field name={fieldNames.type}>
                                            {({ field, form }: FieldProps<WishListType>) => (
                                                <FormControl>
                                                    <FormLabel id="wishList-share-radio-buttons" sx={{ mb: 1 }}>
                                                        <PoolTypography
                                                            typography="subtitle1"
                                                            translationId="app.wishList.share.edit"
                                                        />
                                                    </FormLabel>
                                                    <RadioGroup
                                                        aria-labelledby="wishList-share-radio-buttons"
                                                        value={field.value}
                                                        name="wishList-radio-buttons-group"
                                                        onChange={event =>
                                                            form.setFieldValue(field.name, event.target.value)
                                                        }
                                                        // FIXME there is no MuiRadioGroup declared in ComponentsOverrides
                                                        sx={{ ml: 2, gap: 1.5 }} // Special case (ml), should be aligned with the label in other cases
                                                    >
                                                        <FormControlLabel
                                                            value={WishListType.OrganizationRead}
                                                            control={<Radio />}
                                                            label={
                                                                <Stack>
                                                                    <Box>
                                                                        <FormattedMessage id="app.wishList.share.readType" />
                                                                    </Box>
                                                                    <Box typography="body2" color="grey.800">
                                                                        <FormattedMessage id="app.wishList.share.readTypeDescription" />
                                                                    </Box>
                                                                </Stack>
                                                            }
                                                        />
                                                        <FormControlLabel
                                                            value={WishListType.OrganizationEdit}
                                                            control={<Radio />}
                                                            label={
                                                                <Stack>
                                                                    <Box>
                                                                        <FormattedMessage id="app.wishList.share.editType" />
                                                                    </Box>
                                                                    <Box typography="body2" color="grey.800">
                                                                        <FormattedMessage id="app.wishList.share.editTypeDescription" />
                                                                    </Box>
                                                                </Stack>
                                                            }
                                                        />
                                                    </RadioGroup>
                                                </FormControl>
                                            )}
                                        </Field>
                                    </Stack>
                                )}

                                <Stack gap={1.5}>
                                    <Field name={fieldNames.infoText}>
                                        {({ field }: FieldProps<string>) => (
                                            <PoolTextfield
                                                fullWidth={true}
                                                label={<FormattedMessage id="app.wishList.description.label" />}
                                                multiline={true}
                                                minRows={4}
                                                {...field}
                                            />
                                        )}
                                    </Field>
                                    <PoolBanner variant="outlined">
                                        <FormattedMessage id="app.wishList.edit.description" />
                                    </PoolBanner>
                                </Stack>
                            </Stack>
                        </PoolModalContent>
                        <PoolModalActions>
                            <PoolButton variant="outlined" onClick={() => handleClose(formikProps)}>
                                <FormattedMessage id="app.common.cancel" />
                            </PoolButton>
                            <PoolButton onClick={() => formikProps.handleSubmit()}>
                                <FormattedMessage id="app.common.save" />
                            </PoolButton>
                        </PoolModalActions>
                    </PoolModal>
                );
            }}
        </Formik>
    );
};
